/**
 * Application — core state manager and init orchestrator.
 *
 * Sets window.app as a compat shim so IIFE modules keep working
 * until they are individually migrated to ESM.
 *
 * AI: Entirely written by AI
 * Date: 2026-05-04
 * Model: Claude Sonnet 4.6
 */

import { start as configStart, animations, breakpoints } from './app-config.esm.js';
import { openNewsletter } from './newsletter-subscribe.js';
import { dismiss as notifierDismiss } from './notifier.js';
import { centerActiveJumpNavs } from './jump-nav.js';
import { initConsent } from './consent.js';

// Public Functions
var init,
  get,
  set,
  trapFocus,
  setLastFocused,
  getLastFocused,
  restoreLastFocused,
  observer,
  start,
  bool,
  flags,
  centsToMoney,
  logProduct,
  parseHtml,
  pushState,
  replaceState,
  waitUntil,
  debug,
  log,
  ga,
  sendAnalyticsQueue,
  scrollElementIntoView,
  elementInView,
  onImagesLoaded,
  getBreakpoint;

// General Variables
var application,
  pageSelector,
  pageContentSelector,
  namespace,
  propchange,
  easing,
  properties,
  epStorageDebugKey;

// Element Variables (native DOM — Phase 13.2 dropped the $-prefixed
// jQuery collections; consumers read them off `properties.html` etc.).
var htmlEl, bodyEl, contentEl;

// Main Public Function – Init *********************/
init = function () {
  namespace = 'ThreeSixteen';
  window[namespace] = {};
  application = window[namespace];
  pageSelector = '#page';
  pageContentSelector = '#page-content';
  propchange = namespace + ':propchange';
  htmlEl = document.documentElement;
  bodyEl = document.body;
  contentEl = document.querySelector('.page-content');
  easing = 'cubic-bezier(0.77, 0, 0.175, 1)';
  epStorageDebugKey = 'epAppDebug';

  var appinfo = ep_appinfo,
    rootUrl =
      appinfo.rootUrl.slice('-1') === '/'
        ? appinfo.rootUrl.substr(0, appinfo.rootUrl.length - 1)
        : appinfo.rootUrl,
    shopUrl = rootUrl + appinfo.shopUrl,
    currentPath = window.location.pathname,
    currentUrl = window.location.href,
    cartUrl = rootUrl + '/cart',
    customer = appinfo.customer,
    flags = appinfo.flags,
    newsletter = appinfo.newsletter,
    env = /3sixteen-dev-2|dev\.3sixteen\.com/.test(rootUrl) ? 'staging' : 'live',
    svg = appinfo.svg,
    assets = appinfo.assets,
    domProps = {
      collection: {
        grid: 2,
        denimFilters: {},
      },
      product: {
        cardSelector: '.js-product-card:not(.grid-tout)',
        addItemFormSelector: '.js-add-item-form',
        stickyAtcSelector: '.js-sticky-add-to-cart',
      },
    };

  properties = {
    namespace: namespace,
    i18n: appinfo.i18n,
    html: htmlEl,
    body: bodyEl,
    body_class: bodyEl.className,
    page_type: bodyEl.getAttribute('data-page-type'),
    url: {
      root: rootUrl,
      shop: shopUrl,
      cart: cartUrl,
      current: currentUrl,
      currentObj: new URL(currentUrl),
      initial: currentUrl,
      path: currentPath,
    },
    customer: customer,
    debug: env === 'staging' || window.localStorage.getItem(epStorageDebugKey) === 'true',
    flags: flags,
    newsletter: newsletter,
    header: document.getElementById('header-global'),
    footer: document.getElementById('footer-global'),
    dom: domProps,
    history: window.history,
    env: env,
    isTouch: false,
    isScrollPrevented: false,
    loading: false,
    pageSelector: pageSelector,
    page: document.querySelector(pageSelector),
    pageContentSelector: pageContentSelector,
    pageContent: document.querySelector(pageContentSelector),
    animations: animations(),
    easing: easing,
    cssEasing: {
      easeInOutCubic: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
      easeInOutQuart: 'cubic-bezier(0.77, 0, 0.175, 1)',
      easeInOutQuint: 'cubic-bezier(0.86, 0, 0.07, 1)',
    },
    breakpoints: breakpoints(),
    currentBreakpoint: getBreakpoint(),
    isFlashActive: false,
    isModalActive: false,
    modalActiveName: '',
    isFrontPage: rootUrl === currentUrl.substr(0, currentUrl.length - 1),
    transitionsEnabled: true,
    assets: assets,
    svg: svg,
    videoPlayers: {},
    gaQueue: [],
  };

  application.properties = properties;

  // Mirror onto the API singleton so appGet() / log()'s `a.properties`
  // hydration guard sees a populated object (the guard was checking
  // window.app.properties, but the data lives on window.ThreeSixteen).
  // Pre-init, app.properties is undefined → guards bail correctly. After
  // init, both refs point at the same object, so set/get/observer mutations
  // through app.* are visible to the appGet readers.
  app.properties = properties;

  application.properties.notices = {
    cart: {
      empty: {
        type: 'flash',
        content: app.get('i18n.notifications.cart.empty'),
        force: true,
      },
    },
    newsletter: {
      subscribed: {
        type: 'flash',
        content: app.get('i18n.notifications.newsletter.subscribed'),
        force: true,
      },
    },
  };

  window.addEventListener('resize', function () {
    app.set('currentBreakpoint', getBreakpoint());
  });

  app.observer('loading', onLoading);
  app.observer('body_class', setBodyClass);
  app.observer('page_type', setBodyPageType);
  app.observer('url.currentObj', setUrlProperties);
  app.observer('url.path', setActiveSubNavItem);
  app.observer('isScrollPrevented', setPreventScroll);
  // Phase 13.2: the no-click overlay wires up via native events —
  // cart-drawer and notifier dispatch their own open/close events.
  setupNoClickOverlay();

  document.dispatchEvent(new CustomEvent(namespace + ':init'));

  // Native Shopify Customer Privacy consent adapter (core/consent.esm.js).
  // Populates the CMP-agnostic app.set('consent', …) contract that every
  // downstream gate (app.ga, router pageview, newsletter popup) reads without
  // naming a CMP — see docs/customer-privacy.md.
  initConsent();
  // Drain any pre-consent analytics once statistics consent lands. Keeps gaQueue
  // private to this file — the adapter only writes the consent contract; we react
  // to it here (the same observer pattern the newsletter popup uses).
  app.observer('consent', function () {
    if (app.get('consent.statistics') === true) sendAnalyticsQueue();
  });
  setupFlagsClasses();
  getHemProduct();
  handlePromoBanner();
  handleContrastMode();
  handleHeaderScroll();
  handleNewsletterScrollTrigger();

  document.addEventListener('stateChange', function (event) {
    var data = event.detail || {};
    if (!data.url) return;
    var url = new URL(data.url),
      path = url.hash.length > 0 ? url.pathname + url.hash : url.pathname;

    app.set('url.current', data.url);
    app.set('url.path', path);
  });

  app.start();
};

start = function () {
  configStart();
};

// Public Functions ***********************/
get = function (property) {
  try {
    var separator = separator || '.';

    return property
      .replace('[', separator)
      .replace(']', '')
      .split(separator)
      .reduce(function (obj, prop) {
        return obj[prop];
      }, application.properties);
  } catch (err) {
    if (typeof property !== 'undefined') {
      return application.properties[property];
    } else {
      return application.properties;
    }
  }
};

flags = function (flag) {
  flag = flag.indexOf('flags__') < 0 ? 'flags__' + flag : flag;

  return app.get('flags.' + flag);
};

set = function (property, value) {
  try {
    var schema = application.properties;
    var pList = property.split('.');
    var len = pList.length;
    for (var i = 0; i < len - 1; i++) {
      var elem = pList[i];
      if (!schema[elem]) {
        schema[elem] = {};
      }
      schema = schema[elem];
    }
    schema[pList[len - 1]] = value;
  } catch (err) {
    application.properties[property] = value;
  }
  // propchange is the propagation channel. Native CustomEvent so consumers
  // can subscribe via document.addEventListener without jQuery.
  document.dispatchEvent(
    new CustomEvent(propchange, { detail: { key: property, value: value } })
  );
};

observer = function (property, callback) {
  document.addEventListener(propchange, function (event) {
    var data = event.detail;
    if (property.indexOf(' ') !== -1) {
      var props = property.split(' ');
      for (var i = 0; i < props.length; i++) {
        if (data.key === props[i] && typeof callback === 'function') {
          var eventData = {};
          eventData[props[i]] = data.value;
          callback(event, eventData);
        }
      }
    }
    if (data.key === property && typeof callback === 'function') {
      callback(event, data.value);
    }
  });
};

// Bool - returns true if one property is true, false if all are false
bool = function (property, callback) {
  var props = property.split(' '),
    propsObj = {};
  for (var i = 0; i < props.length; i++) {
    propsObj[props[i]] = app.get(props[i]);
  }
  var keysArray = Object.keys(propsObj);

  document.addEventListener(propchange, function (event) {
    var data = event.detail;
    if (keysArray.indexOf(data.key) !== -1) {
      propsObj[data.key] = data.value;
      var anyTrue = Object.values(propsObj).some(function (v) { return v === true; });
      callback(event, anyTrue);
    }
  });
};

// waitUntil — native rewrite. `element` is now a DOM Element; `test`
// is a CSS selector or a predicate function evaluated against the element.
waitUntil = function (element, test, expected, callback, counter) {
  if (typeof counter === 'undefined') counter = 0;
  var passes = typeof test === 'function' ? test(element) : element.matches(test);
  if (passes === expected && typeof callback === 'function') {
    callback();
  } else if (counter < 30) {
    setTimeout(function () {
      waitUntil(element, test, expected, callback, counter + 1);
    }, 20);
  } else {
    console.log('waitUntil failed: ' + test + ' === ' + expected);
  }
};

debug = function () {
  app.set('debug', true);
  window.localStorage.setItem(epStorageDebugKey, true);
  return '>> DEBUG MODE ENABLED . . .';
};

/**
 * Debug logger with automatic tag coloring.
 *
 * Usage:
 *   app.log('plain string')           → console.log('plain string')
 *   app.log('[Cart]', 'built', data)  → colored [Cart] tag + remaining args
 *   app.log('%cManual', 'color:red')  → passthrough (existing %c usage)
 *
 * AI: Entirely written by AI
 * Date: 2026-03-16
 * Model: Claude Opus 4.6
 */
log = (function () {
  var tagColors = {
    // Red — errors only
    Error: '#e74c3c',
    // Amber — standalone features / filters
    Maps: '#d68910',
    Filter: '#d68910',
    // Green — cart family
    Cart: '#2ecc71',
    MiniCart: '#2ecc71',
    CartIconBubble: '#2ecc71',
    // Teal — data & input (green → blue bridge)
    Analytics: '#1abc9c',
    Consent: '#16a085',
    Form: '#17a589',
    Forms: '#17a589',
    FormState: '#17a589',
    // Blue — navigation & actions
    'Add to Cart': '#3498db',
    Router: '#2e86c1',
    AjaxRouter: '#2e86c1',
    Collection: '#5dade2',
    CollectionOverlay: '#5dade2',
    CollectionList: '#5dade2',
    // Indigo — UI components (blue → purple bridge)
    Slider: '#6c5ce7',
    ImageSlider: '#6c5ce7',
    ExpandableList: '#6c5ce7',
    VideoPlayers: '#6c5ce7',
    ProductCard: '#6c5ce7',
    // Purple — product services
    Hemming: '#8e44ad',
    SalePolicy: '#8e44ad',
    SizingModalOpener: '#8e44ad',
    Waitlist: '#9b59b6',
    // Pink — outbound / notifier surfaces
    Newsletter: '#e91e63',
    NewsletterSubscribe: '#e91e63',
    Notice: '#e91e63',
    Notifier: '#e91e63',
    // Blurple — boot identity (which theme are we running?)
    Theme: '#5865f2',
    // Unknown tags fall through to `defaultColor` (gray) — no need to
    // enumerate every component name; the first-word fallback below also
    // routes namespaced tags (e.g. `[Waitlist API]` → `Waitlist`).
  };

  var tagPattern = /^\[([^\]]+)\]/;
  var defaultColor = '#95a5a6';

  return function () {
    if (app.get('debug') === false) return;

    var args = Array.prototype.slice.call(arguments);

    // passthrough: already using %c or no arguments
    if (!args.length || (typeof args[0] === 'string' && args[0].indexOf('%c') === 0)) {
      console.log.apply(console, args);
      return;
    }

    // auto-color [Tag] prefixes
    if (typeof args[0] === 'string') {
      var match = args[0].match(tagPattern);
      if (match) {
        var tag = match[1];
        var rest = args[0].slice(match[0].length).replace(/^\s/, '');
        // Exact tag first; fall back to the first space-delimited word so
        // multi-word tags like "Waitlist API" can degrade to "Waitlist".
        var color = tagColors[tag] || tagColors[tag.split(' ')[0]] || defaultColor;
        var styledTag = '%c[' + tag + ']%c ' + rest;
        var tagStyle = 'color:' + color + ';font-weight:bold';
        var resetStyle = 'color:inherit;font-weight:normal';
        args.splice(0, 1, styledTag, tagStyle, resetStyle);
        console.log.apply(console, args);
        return;
      }
    }

    // plain fallback
    console.log.apply(console, args);
  };
})();

ga = function (type, action, data, logStr) {
  // Single analytics chokepoint. The consent check reads the CMP-agnostic
  // app-state contract (set by the consent adapter), never a CMP global —
  // fail-closed: app.get('consent.statistics') is falsy until the adapter
  // affirmatively sets it, so nothing fires before consent is known.
  if (app.get('consent.statistics') && typeof window.ga !== 'undefined') {
    console.log(type, action, data, logStr);
    if (typeof data !== 'undefined' && data !== null) {
      window.ga(type, action, data);
    } else {
      window.ga(type, action);
    }
    if (typeof logStr === 'string') {
      app.log(logStr);
    }
  }
  // Consent not yet resolved (or denied) — queue requests. The queue is only
  // flushed by sendAnalyticsQueue() when statistics consent is present; if
  // consent resolves to denied it is never flushed, so queued events are
  // discarded, not sent.
  else if (typeof window.ga !== 'undefined') {
    var queueElement = {
      type: type,
      action: action,
      data: data,
      logStr: logStr,
    };

    app.get('gaQueue').push(queueElement);
  }
};

sendAnalyticsQueue = function () {
  app.log('Sending analytics queue . . .');
  var queue = app.get('gaQueue').splice(0);
  queue.forEach(function (element) {
    app.ga(element.type, element.action, element.data, element.logStr);
  });
};

getBreakpoint = function () {
  var bp = false;
  var bpMap = breakpoints();
  Object.keys(bpMap).forEach(function (key) {
    if (key === 'small' || key.toLowerCase().indexOf('up') > -1 || key === 'xxLarge') {
      if (bpMap[key].matches) bp = key;
    }
  });
  return bp ? bp.replace('Up', '') : false;
};

scrollElementIntoView = function (element, callback) {
  var elementIsInView = app.elementInView(element);
  if (!elementIsInView) {
    ajaxRouter.scrollToAnchor(element, callback);
  } else if (typeof callback === 'function') {
    callback();
  }
};

elementInView = function (element) {
  var el = unwrapEl(element) || element;
  if (!el || typeof el.getBoundingClientRect !== 'function') return false;
  var headerEl = app.get('header');
  var headerHeight = headerEl ? headerEl.offsetHeight : 0;
  var rect = el.getBoundingClientRect();
  var viewportTop = window.scrollY + headerHeight;
  var viewportBottom = viewportTop + window.innerHeight;
  var elementTop = rect.top + window.scrollY;
  var elementBottom = elementTop + el.offsetHeight;
  return elementBottom > viewportTop && elementTop < viewportBottom;
};

/**
 * Attach load/error handlers to a collection of <img> elements, ignoring
 * already-cached images. Handlers self-remove once every pending image
 * settles. Native rewrite — accepts a NodeList / Array of <img> elements.
 *
 * AI: Mostly written by AI
 * Date: 2025-12-05
 * Updated: 2026-05-08 (Claude Opus 4.7) — Phase 13.2 native rewrite.
 * Model: Claude Opus 4.5
 *
 * @param {NodeList|Array<HTMLImageElement>} images
 * @param {Object} options
 * @param {Function} options.onLoad — called per non-cached image on `load`
 * @param {Function} options.onError — called per image on `error` (default = onLoad)
 * @param {Function} options.onAllCached — called when every image was cached
 */
onImagesLoaded = function (images, options) {
  var pendingImages = 0;
  var onLoad = options.onLoad;
  var onError = options.onError || onLoad;
  var onAllCached = options.onAllCached;
  var list = images && images.length ? Array.prototype.slice.call(images) : [];

  list.forEach(function (img) {
    if (img.complete && img.naturalHeight !== 0) return;
    pendingImages += 1;
    var handlerLoad, handlerError;
    handlerLoad = function () {
      img.removeEventListener('load', handlerLoad);
      img.removeEventListener('error', handlerError);
      pendingImages -= 1;
      if (typeof onLoad === 'function') onLoad.call(img);
    };
    handlerError = function () {
      img.removeEventListener('load', handlerLoad);
      img.removeEventListener('error', handlerError);
      pendingImages -= 1;
      if (typeof onError === 'function') onError.call(img);
    };
    img.addEventListener('load', handlerLoad);
    img.addEventListener('error', handlerError);
  });

  if (pendingImages === 0 && typeof onAllCached === 'function') {
    requestAnimationFrame(onAllCached);
  }
};

// Native trap-focus. Phase 13 replacement of the jQuery-based version
// (which relied on `:tabbable`-style selectors and `.off('keydown.trapFocus')`
// namespacing). Accepts a DOM element or a jQuery collection (for any
// remaining IIFE-era callers); the WeakMap-tracked handlers replace the
// namespace for off-before-on de-duplication so persistent DOM (mini-cart,
// mobile nav, filter overlay) doesn't stack handlers across re-traps.
var TAB_KEY = 'Tab';
var TRAPPABLE_SELECTOR =
  'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="email"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])';
var trapHandlers = new WeakMap();

function unwrapEl(maybe) {
  if (maybe == null) return null;
  if (maybe.nodeType) return maybe;
  if (maybe.jquery && maybe[0]) return maybe[0];
  return null;
}

trapFocus = function (element) {
  var root = unwrapEl(element);
  if (!root) {
    app.restoreLastFocused();
    return;
  }
  var tabbable = root.querySelectorAll(TRAPPABLE_SELECTOR);
  if (tabbable.length === 0) return;
  var first = tabbable[0];
  var last = tabbable[tabbable.length - 1];
  first.focus();

  // Tear down any prior trap on these specific endpoints before re-binding.
  var prior = trapHandlers.get(root);
  if (prior) {
    prior.first.removeEventListener('keydown', prior.firstHandler);
    prior.last.removeEventListener('keydown', prior.lastHandler);
  }

  function lastHandler(e) {
    if ((e.key === TAB_KEY || e.keyCode === 9) && !e.shiftKey) {
      e.preventDefault();
      first.focus();
    }
  }
  function firstHandler(e) {
    if ((e.key === TAB_KEY || e.keyCode === 9) && e.shiftKey) {
      e.preventDefault();
      last.focus();
    }
  }
  last.addEventListener('keydown', lastHandler);
  first.addEventListener('keydown', firstHandler);
  trapHandlers.set(root, { first: first, last: last, firstHandler: firstHandler, lastHandler: lastHandler });
};

setLastFocused = function () {
  app.set('lastFocused', document.activeElement);
};

getLastFocused = function () {
  return app.get('lastFocused');
};

restoreLastFocused = function () {
  var last = app.get('lastFocused');
  // Can be called without a prior setLastFocused (e.g. notifier auto-close
  // when no modal was opened), in which case lastFocused is undefined.
  if (last && typeof last.focus === 'function') {
    last.focus();
  }
};

centsToMoney = function (cents) {
  // Native replacement for accounting.formatMoney (default '$0.00' format
  // with comma thousand separators). Output kept identical to preserve
  // the .replace(/\.00$/, '') drop-trailing-cents convention.
  //
  // LEGACY-COMPAT: this hardcodes a USD-style format and is unsafe on
  // Markets-enabled storefronts (the visitor's market currency may use
  // commas as decimal separators and a different symbol). No first-party
  // theme code calls it after the Markets cutover; the export is kept
  // only so any merchant inline-script or third-party app that referenced
  // window.app.centsToMoney doesn't break on this deploy. Prefer the
  // Markets-aware `{{ x | money }}` filter at render time for any new
  // money formatting.
  var fixed = (cents / 100).toFixed(2);
  var formatted = '$' + fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return '<span class="money">' + formatted.replace(/\.00$/, '') + '</span>';
};

parseHtml = function (html) {
  // Native replacement for jQuery.parseHTML — returns the parsed nodes
  // as an Array. Scripts inside `html` are NOT executed (intentional;
  // jQuery.parseHTML's third arg `true` allowed script execution which
  // first-party callers never relied on).
  var doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.prototype.slice.call(doc.body.childNodes);
};

logProduct = function () {
  var bodyRef = app.get('body') || document.body;
  var script = bodyRef.querySelector(
    'form.js-add-item-form script[type="application/json"][data-variant-data]'
  );
  if (script) {
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return undefined;
    }
  }
};

pushState = function (stateObj, title, url) {
  app.get('history').pushState(stateObj, title, url);
  document.dispatchEvent(
    new CustomEvent('stateChange', { detail: { state: stateObj, title: title, url: url } })
  );
};

replaceState = function (stateObj, title, url) {
  app.get('history').replaceState(stateObj, title, url);
  document.dispatchEvent(
    new CustomEvent('stateChange', { detail: { state: stateObj, title: title, url: url } })
  );
};

// Private Functions ***********************/
function onLoading(event, loading) {
  var loadingClass = 'page--loading';
  app.set('isScrollPrevented', loading);
  htmlEl.classList.toggle(loadingClass, loading);
}

function setBodyClass(event, body_class) {
  bodyEl.className = body_class;
}

function setBodyPageType(event, page_type) {
  bodyEl.setAttribute('data-page-type', page_type);
}

function getHemProduct() {
  var hemHandle = 'chainstitch-hemming';
  Shopify.getProduct(hemHandle, function (hemProduct) {
    app.set('data.hemProduct', hemProduct);
  });
}

// The header no longer auto-hides on scroll — it stays sticky and instead
// crossfades its full wordmark to the compact mark once the page leaves the
// top. This toggles `header-scrolled` on <html>; the opacity crossfade lives
// in _header.scss (.header-logo-full / .header-logo-mark). Threshold tracks the
// header height so the swap lands roughly as the header clears its own depth.
function handleHeaderScroll() {
  var scrolledClass = 'header-scrolled';

  function update() {
    var threshold = app.get('breakpoints.largeUp.matches') ? 80 : 60;
    htmlEl.classList.toggle(scrolledClass, window.scrollY > threshold);
  }

  update();
  window.addEventListener('scroll', update, { passive: true });
}

function setupFlagsClasses() {
  var flagsObj = app.get('flags') || {};
  Object.keys(flagsObj).forEach(function (key) {
    htmlEl.classList.toggle(key.replace('flags__', '').replace('_', '-'), flagsObj[key] === true);
  });
}

// TODO: Add preflight test coverage (application.test.js)
function setUrlProperties(event, currentObj) {
  app.set('url.current', currentObj.href);
  app.set('url.path', currentObj.pathname);
  app.set(
    'isFrontPage',
    app.get('url.root') === app.get('url.current').substr(0, app.get('url.current').length - 1)
  );
}

function setPreventScroll(event, isScrollPrevented) {
  htmlEl.classList.toggle('no-scroll', !!isScrollPrevented);
}

// TODO: Add preflight test coverage (application.test.js)
//
// Two jobs on every navigation: (1) maintain the `current-menu-item` class on
// the matched nav-list (for in-page nav swaps that don't re-render server-side),
// and (2) re-center the jump nav. The centering is delegated to the shared
// `centerActiveJumpNavs` primitive, which keys off each jump nav's own
// `.current-menu-item` rather than the global `a[href]` lookup below — that
// lookup can resolve to a header/sidebar link before the jump nav's, which is
// why the inline version it replaced never fired the recenter reliably.
function setActiveSubNavItem(event, path) {
  var currentMenuItemClass = 'current-menu-item';
  var changeToItem = bodyEl.querySelector('a[href="' + path + '"]');
  var parentLi =
    changeToItem && changeToItem.parentElement && changeToItem.parentElement.matches('li')
      ? changeToItem.parentElement
      : null;
  var canChange = !!parentLi && !!changeToItem.closest('.nav-list.show-current-menu-item');

  // canChange already implies parentLi is non-null (it's the first conjunct).
  if (canChange) {
    parentLi.classList.add(currentMenuItemClass);
    var sibling = parentLi.parentElement ? parentLi.parentElement.firstElementChild : null;
    while (sibling) {
      if (sibling !== parentLi) sibling.classList.remove(currentMenuItemClass);
      sibling = sibling.nextElementSibling;
    }
  }

  // Runs after the class management above so an in-page nav swap that moves
  // `current-menu-item` centers on the new active item. A no-op off small/
  // medium viewports and on pages without a jump nav.
  centerActiveJumpNavs();
}

// No-click overlay coordinator — Phase 13.2 native event-based rewrite.
// Tracks the two openable surfaces (cart drawer + notifier modal) via
// their own native events and shows/hides the #no-click veil when at
// least one is open.
var openOverlayCount = 0;
var noClickClickHandler = null;

function setupNoClickOverlay() {
  document.addEventListener('cart-drawer:open', incrementNoClick);
  document.addEventListener('cart-drawer:close', decrementNoClick);
  document.addEventListener('notifier:open', incrementNoClick);
  document.addEventListener('notifier:close', decrementNoClick);
}

function incrementNoClick() {
  openOverlayCount += 1;
  if (openOverlayCount === 1) showNoClick();
}

function decrementNoClick() {
  openOverlayCount = Math.max(0, openOverlayCount - 1);
  if (openOverlayCount === 0) hideNoClick();
}

function showNoClick() {
  var noClickEl = document.querySelector('#no-click');
  if (!noClickEl) return;
  var modalProps = app.get('animations.modal');
  app.set('isScrollPrevented', true);
  noClickClickHandler = function (event) {
    if (event.target.closest('#no-click')) handleNoClick(event);
  };
  document.addEventListener('click', noClickClickHandler);
  CoreAnim.fadeIn(noClickEl, { duration: modalProps.in.duration });
}

function hideNoClick() {
  var noClickEl = document.querySelector('#no-click');
  var modalProps = app.get('animations.modal');
  app.set('isScrollPrevented', false);
  if (noClickClickHandler) {
    document.removeEventListener('click', noClickClickHandler);
    noClickClickHandler = null;
  }
  if (noClickEl) CoreAnim.fadeOut(noClickEl, { duration: modalProps.out.duration });
}

function handleNoClick(event) {
  event.preventDefault();
  // Dismiss whichever surface is currently open. Querying the elements
  // directly removes the dependency on `app.get('isMiniCartOpen' /
  // 'isModalActive')` — those properties no longer drive this overlay.
  var cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && cartDrawer.isOpen && typeof cartDrawer.close === 'function') {
    cartDrawer.close();
  }
  notifierDismiss();
}

function handlePromoBanner() {
  var closeBannerSelector = '.js-close-promo-banner',
    hasBannerClass = 'has-promo-banner';

  document.addEventListener('click', function (event) {
    var btn = event.target.closest(closeBannerSelector);
    if (!btn) return;
    var banner = btn.closest('#promo-banner');
    if (!banner) return;
    htmlEl.classList.remove(hasBannerClass);
    CoreAnim.slideUp(banner, { duration: 300 }).then(function () {
      banner.remove();
    });
  });
}

// Wraps localStorage / sessionStorage so callers don't need their own
// try/catch for private-mode, quota, or disabled-storage failures.
function safeStorage(store) {
  return {
    get: function (key) {
      try { return store.getItem(key); } catch (e) { return null; }
    },
    set: function (key, value) {
      try { store.setItem(key, value); } catch (e) {}
    },
    remove: function (key) {
      try { store.removeItem(key); } catch (e) {}
    }
  };
}

function handleContrastMode() {
  var contrastModeSelector = '[name="js-contrast-mode-enable"]',
    contrastModeClass = 'color-theme--hc',
    storageKey = 'ep-contrast-enable',
    ls = safeStorage(window.localStorage);

  document.addEventListener('change', function (event) {
    var input = event.target.closest(contrastModeSelector);
    if (!input) return;
    var checked = input.checked;
    htmlEl.classList.toggle(contrastModeClass, checked);
    ls.set(storageKey, String(checked));
  });

  if (ls.get(storageKey) === 'true') {
    var input = document.querySelector(contrastModeSelector);
    if (input) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

/**
 * Auto-opens the newsletter signup on non-product surfaces once the visitor
 * has shown engagement. Every eligible page fires after 10s on page; the
 * homepage additionally fires the moment the visitor scrolls past the
 * first feature inside #homepage-features (`.js-feature` blocks),
 * whichever comes first.
 *
 * Visitors who land on an excluded surface (product, cart, account, 404)
 * miss their chance to see it there, so we record the visit in
 * sessionStorage and fire immediately on the first eligible page they hit
 * next — no 10s wait, no scroll required.
 *
 * Suppressed for 14 days after dismiss/submit (configurable via the
 * newsletter_suppress_days setting; 0 disables suppression).
 *
 * Gated on cookie consent being resolved (app.get('consent.ready'), set by the
 * consent adapter) so the popup never overlaps the consent banner. This is a
 * layout gate, not a tracking gate — it keys on `ready`, not
 * `statistics`. Suppression is stored in localStorage (UI preference, never
 * sent to the server) so it works regardless of consent choices.
 *
 * AI: Entirely written by AI
 * Date: 2026-04-26
 * Model: Claude Opus 4.7
 */
function handleNewsletterScrollTrigger() {
  if (app.flags('ep_newsletter_popup_enable') !== true) return;

  var storageKey = 'ep-newsletter-shown-at',
    subscribedKey = 'ep-newsletter-subscribed',
    pendingKey = 'ep-newsletter-pending',
    suppressDays = app.get('newsletter.suppressDays'),
    suppressMs = (typeof suppressDays === 'number' ? suppressDays : 14) * 24 * 60 * 60 * 1000,
    waitSeconds = app.get('newsletter.waitSeconds'),
    timerMs = (typeof waitSeconds === 'number' ? waitSeconds : 10) * 1000,
    firstFeatureSelector = '#homepage-features .js-feature',
    ls = safeStorage(window.localStorage),
    ss = safeStorage(window.sessionStorage),
    timeoutId = null,
    scrollHandler = null;

  function markShown() {
    ls.set(storageKey, String(Date.now()));
  }

  function isSuppressed() {
    // A successful submission suppresses the auto-popup permanently.
    if (ls.get(subscribedKey) === '1') return true;
    var shownAt = parseInt(ls.get(storageKey), 10);
    return shownAt > 0 && Date.now() - shownAt < suppressMs;
  }

  function isExcludedPage() {
    var pt = app.get('page_type') || '';
    // 'password' was previously gated at the Liquid layer (the inline
    // `<template id="tpl-newsletter-signup">` was wrapped in
    // `{% unless template.name == 'password' %}`). With the modal-section
    // migration that template is gone, so the gate must live here — the
    // auto-popup would otherwise fire on the password splash and either
    // render over the brand experience or fire a wasted fetch + GA
    // Impression on every password-page visit.
    return (
      pt === 'cart' ||
      pt === '404' ||
      pt === 'password' ||
      pt.indexOf('product') === 0 ||
      pt.indexOf('customers') === 0
    );
  }

  var resizeHandler = null;

  function cleanup() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler);
      resizeHandler = null;
    }
  }

  function trigger() {
    cleanup();
    if (document.getElementById('newsletter-signup')) return;
    markShown();
    openNewsletter({ source: 'auto-popup' });
  }

  function arm() {
    cleanup();
    // Record excluded-page visits regardless of consent — sessionStorage
    // is a per-tab UI signal, not tracking — so the next eligible page can
    // fire immediately.
    if (isExcludedPage()) {
      ss.set(pendingKey, '1');
      return;
    }
    // Wait for consent to resolve so the popup never overlaps the consent
    // banner — the two must never show at once. consent.ready is guaranteed to
    // resolve via consent.whenReady's timeout, so no fallback is needed here:
    // while a banner is still up, ready stays false and the popup simply waits.
    if (!app.get('consent.ready')) return;
    if (isSuppressed()) return;

    if (ss.get(pendingKey) === '1') {
      ss.remove(pendingKey);
      trigger();
      return;
    }

    timeoutId = setTimeout(trigger, timerMs);

    if (app.get('page_type') === 'index') {
      var firstFeature = document.querySelector(firstFeatureSelector);
      if (!firstFeature) return;
      var pastFeature = firstFeature.getBoundingClientRect().top + window.scrollY + firstFeature.offsetHeight;
      scrollHandler = function () {
        if (window.scrollY >= pastFeature) trigger();
      };
      resizeHandler = function () {
        pastFeature = firstFeature.getBoundingClientRect().top + window.scrollY + firstFeature.offsetHeight;
      };
      window.addEventListener('scroll', scrollHandler);
      window.addEventListener('resize', resizeHandler);
    }
  }

  document.addEventListener('submit', function (event) {
    if (event.target.closest('[data-newsletter-subscribe]')) markShown();
  });
  document.addEventListener('click', function (event) {
    if (event.target.closest('.js-newsletter-close')) markShown();
  });

  arm();
  app.observer('consent', arm);
  app.observer('page_type', arm);
}

var app = {
  init: init,
  get: get,
  set: set,
  flags: flags,
  observer: observer,
  trapFocus: trapFocus,
  getLastFocused: getLastFocused,
  setLastFocused: setLastFocused,
  restoreLastFocused: restoreLastFocused,
  bool: bool,
  waitUntil: waitUntil,
  debug: debug,
  log: log,
  ga: ga,
  getBreakpoint: getBreakpoint,
  scrollElementIntoView: scrollElementIntoView,
  elementInView: elementInView,
  onImagesLoaded: onImagesLoaded,
  centsToMoney: centsToMoney,
  parseHtml: parseHtml,
  logProduct: logProduct,
  pushState: pushState,
  replaceState: replaceState,
  start: start,
};

window.app = app;

// Phase 13.2: dropped the jQuery window-global shims. The IIFE concat
// pipeline that needed them is gone; every ESM caller uses native
// document.* / window.* directly.

document.addEventListener('DOMContentLoaded', function () {
  app.init();
});

export { app };
